// Updated src/components/sections/Projects/Projects.jsx
import React, { useState, useEffect } from 'react';
import { Section, Container } from '../../common/UI';
import ProjectCard from './ProjectCard';
import ProjectFilter from './ProjectFilter';
import ProjectModal from './ProjectModal';
import { getProjects } from '../../../services/contentful';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch projects from Contentful
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const contentfulProjects = await getProjects();
        
        if (contentfulProjects.length === 0) {
          // Fallback to mock data if no Contentful projects
          console.warn('No projects found in Contentful, using fallback data');
          const mockProjects = [
            {
              id: 'mock-1',
              title: 'withKM Platform',
              description: 'Comprehensive business platform that I co-founded, featuring user management, analytics, and automation tools.',
              image: '/images/projects/withkm-platform.jpg',
              technologies: ['React.js', 'Node.js', 'MongoDB', 'Express.js'],
              githubURL: 'https://github.com/yourusername/withkm-platform',
              liveURL: 'https://withkm.com',
              category: 'Web Development',
              featured: true,
              status: 'Completed'
            },
            {
              id: 'mock-2',
              title: 'Wealth Gambit Media Backend',
              description: 'Robust backend infrastructure for media content management, currently working as backend developer.',
              image: '/images/projects/wg-media-backend.jpg',
              technologies: ['Node.js', 'Express.js', 'PostgreSQL', 'Docker'],
              githubURL: 'https://github.com/yourusername/wg-media-backend',
              liveURL: 'https://wgmedia.com',
              category: 'Backend Development',
              featured: true,
              status: 'In Progress'
            },
            {
              id: 'mock-3',
              title: 'Game Development Project',
              description: 'Indie puzzle game showcasing my passion for game development using Unity and C#.',
              image: '/images/projects/puzzle-game.jpg',
              technologies: ['Unity', 'C#', 'Blender', 'Photoshop'],
              githubURL: 'https://github.com/yourusername/puzzle-game',
              liveURL: 'https://itch.io/puzzle-adventure',
              category: 'Game Development',
              featured: true,
              status: 'In Progress'
            }
          ];
          setProjects(mockProjects);
          setFilteredProjects(mockProjects);
        } else {
          setProjects(contentfulProjects);
          setFilteredProjects(contentfulProjects);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError('Failed to load projects. Please try again later.');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filter projects by category
  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category === selectedCategory));
    }
  }, [selectedCategory, projects]);

  const categories = ['All', ...new Set(projects.map(project => project.category))];
  const featuredProjects = projects.filter(project => project.featured);

  if (error) {
    return (
      <Section id="projects" background="default" padding="default">
        <Container>
          <div className="text-center py-16">
            <div className="text-red-500 mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Oops! Something went wrong
            </h3>
            <p className="text-gray-600 dark:text-gray-300">{error}</p>
          </div>
        </Container>
      </Section>
    );
  }

  return (
    <Section id="projects" background="default" padding="default">
      <Container>
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Featured Projects
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Showcasing my passion for creating innovative solutions across web, mobile, and game development
          </p>
        </div>

        {/* Featured Projects Highlight */}
        {featuredProjects.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              🌟 Featured Work
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => setSelectedProject(project)}
                  featured={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Project Filter */}
        <ProjectFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="text-gray-600 dark:text-gray-300 mt-4">Loading projects...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => setSelectedProject(project)}
                featured={false}
              />
            ))}
          </div>
        )}

        {/* Project Modal */}
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </Container>
    </Section>
  );
};

export default Projects;